import Badge from "@/components/base/Badge";
import type { CheckedAllocation } from "@/services/paymentService";
import { formatMoney } from "@/utils/format";
import type { FeeLine } from "@/types";

interface Props {
  /** Frais encore dus, dans l'ordre de la fiche. */
  lines: FeeLine[];
  checked: ReadonlySet<string>;
  onToggle: (key: string) => void;
  allocation: CheckedAllocation;
  /** Montant saisi par l'utilisateur. */
  amount: number;
  /** Titre de la colonne du montant dû (« Montant » à l'inscription, « Reste dû » ensuite). */
  dueLabel?: string;
}

/**
 * Frais à cocher par l'utilisateur : le versement va d'abord sur les frais cochés ; un surplus va sur la
 * tranche suivante encore due (signalée « surplus ») ; un montant au-delà de tout le reste dû est refusé.
 */
export default function FeeChecklist({ lines, checked, onToggle, allocation, amount, dueLabel = "Reste dû" }: Props) {
  const payable = lines.filter((l) => l.remaining > 0);
  const allocated = Object.values(allocation.amounts).reduce((s, v) => s + v, 0);
  const checkedLeft = payable
    .filter((l) => checked.has(l.key))
    .reduce((s, l) => s + l.remaining - (allocation.amounts[l.key] ?? 0), 0);

  if (payable.length === 0) {
    return <p className="rounded-md border border-dashed border-background-300 py-6 text-center text-xs text-foreground-400">Tous les frais sont soldés.</p>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-background-200">
      <table className="w-full text-sm">
        <thead className="bg-background-100">
          <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
            <th className="w-10 px-3 py-2 font-medium">
              <span className="sr-only">Cocher</span>
            </th>
            <th className="px-2 py-2 font-medium">Frais réglés par ce versement</th>
            <th className="px-3 py-2 text-right font-medium">{dueLabel}</th>
            <th className="px-3 py-2 text-right font-medium">Versé maintenant</th>
          </tr>
        </thead>
        <tbody>
          {payable.map((l) => {
            const isChecked = checked.has(l.key);
            const isOverflow = allocation.overflowKeys.includes(l.key);
            const part = allocation.amounts[l.key] ?? 0;
            return (
              <tr
                key={l.key}
                onClick={() => onToggle(l.key)}
                className={`cursor-pointer border-t border-background-200 transition-colors ${
                  isChecked ? "bg-primary-50/60" : isOverflow ? "bg-secondary-50/70" : "hover:bg-background-100/60"
                }`}
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isChecked || isOverflow}
                    onChange={() => onToggle(l.key)}
                    aria-label={`Régler « ${l.label} »`}
                    className="h-4 w-4 cursor-pointer accent-primary-600"
                  />
                </td>
                <td className="px-2 py-2 text-foreground-800">
                  {l.label}
                  {l.dueDate && <span className="ml-1.5 text-xs text-foreground-400">{l.dueDate}</span>}
                  {isOverflow && (
                    <Badge tone="secondary" className="ml-2">
                      surplus
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground-700">{formatMoney(l.remaining)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-primary-700">{part > 0 ? formatMoney(part) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-background-200 bg-background-100/60 text-xs">
            <td colSpan={2} className="px-3 py-2 text-foreground-600">
              Reste à payer de l'élève : <strong className="text-foreground-900">{formatMoney(allocation.totalRemaining)}</strong>
            </td>
            <td colSpan={2} className="px-3 py-2 text-right text-foreground-600">
              Total du versement : <strong className="text-foreground-900">{formatMoney(allocated)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
      {amount > 0 && allocation.excess === 0 && checked.size > 0 && checkedLeft > 0 && allocation.overflowKeys.length === 0 && (
        <p className="border-t border-background-200 px-3 py-2 text-xs text-foreground-500">
          Après ce versement, il restera {formatMoney(checkedLeft)} à payer sur les frais cochés.
        </p>
      )}
      {allocation.overflowKeys.length > 0 && (
        <p className="border-t border-background-200 bg-secondary-50/60 px-3 py-2 text-xs text-secondary-800">
          Le montant dépasse les frais cochés : le surplus est reporté sur la suite de la fiche (mention « surplus »).
        </p>
      )}
    </div>
  );
}
